import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, RadialBarChart, RadialBar, PolarAngleAxis
} from 'recharts';
import {
  Home, Play, Trophy, Gamepad2, Target, Crown, Sparkles, Flame,
  Users, Zap, TrendingUp, Award, BarChart3, Activity, Star,
  Search, ChevronLeft, ChevronRight, X, Crosshair
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import useGameStore from '../store/gameStore';
import BirdMascot from '../components/BirdMascot';
import BirdAvatar from '../components/BirdAvatar';
import MostReactedGallery from '../components/MostReactedGallery';

const CHART_COLORS = ['#22D3EE', '#FB7242', '#FACC15', '#34D399', '#60A5FA', '#F472B6', '#A3E635'];
const PRIMARY = '#22D3EE';
const ACCENT = '#FB7242';
const GOLD = '#FACC15';
const GREEN = '#34D399';

// Point-based color tiers for the global rankings list
const TIERS = [
  { name: 'Legend', min: 5000, color: '#FACC15' },
  { name: 'Diamond', min: 2500, color: '#22D3EE' },
  { name: 'Gold', min: 1000, color: '#34D399' },
  { name: 'Silver', min: 500, color: '#60A5FA' },
  { name: 'Bronze', min: 100, color: '#FB7242' },
  { name: 'Rookie', min: 0, color: '#94A3B8' },
];
const getTier = (points) => TIERS.find((t) => points >= t.min) || TIERS[TIERS.length - 1];

// Animated number counter
const CountUp = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = React.useState(0);
  React.useEffect(() => {
    let start = null;
    const target = Number(value) || 0;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(target);
    };
    requestAnimationFrame(step);
  }, [value, duration]);
  return <span>{display.toLocaleString()}</span>;
};

const ChartTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-primary/30 bg-card/95 backdrop-blur-sm px-3 py-2 shadow-xl">
      {label !== undefined && <div className="text-xs text-muted-foreground mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="text-sm font-semibold" style={{ color: p.color || p.fill }}>
          {p.name}: {Number(p.value).toLocaleString()}{suffix}
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, delay, suffix = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Card className="p-4 sm:p-5 bg-gradient-card border-border relative overflow-hidden h-full">
      <div className="absolute -right-4 -top-4 opacity-10">
        <Icon className="w-20 h-20" style={{ color: accent }} />
      </div>
      <div className="relative">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg mb-3" style={{ backgroundColor: `${accent}22` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold" style={{ color: accent }}>
          <CountUp value={value} />{suffix}
        </div>
        <div className="text-xs sm:text-sm text-muted-foreground mt-1">{label}</div>
      </div>
    </Card>
  </motion.div>
);

const HallOfFameCard = ({ icon: Icon, title, player, metric, metricLabel, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay }}
    whileHover={{ y: -4 }}
    data-testid={`hall-of-fame-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Card className="p-5 bg-gradient-card border-border h-full" style={{ borderColor: `${accent}40` }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5" style={{ color: accent }} />
        <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: accent }}>{title}</span>
      </div>
      {player ? (
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full blur-md opacity-40" style={{ backgroundColor: accent }} />
            <div className="relative">
              <BirdAvatar avatarId={player.avatar_id || 1} size={52} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-lg truncate">{player.nickname}</div>
            <div className="text-2xl font-heading font-bold" style={{ color: accent }}>
              {typeof metric === 'number' ? metric.toLocaleString() : metric}
            </div>
            <div className="text-xs text-muted-foreground">{metricLabel}</div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground py-4 text-center">No data yet</div>
      )}
    </Card>
  </motion.div>
);

const RankRow = ({ player, rank, metric, metricLabel, accent }) => {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  return (
    <motion.div
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.04 }}
      whileHover={{ x: 4 }}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
        rank <= 3 ? 'bg-muted/50 border-primary/30' : 'bg-card border-border hover:border-primary/20'
      }`}
    >
      <div className="w-8 text-center shrink-0">
        {medal ? <span className="text-lg">{medal}</span> : <span className="text-sm font-bold text-muted-foreground">#{rank}</span>}
      </div>
      <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate text-sm sm:text-base">{player.nickname}</div>
        <div className="text-xs text-muted-foreground">{metricLabel}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-heading font-bold text-base sm:text-lg" style={{ color: accent }}>
          {typeof metric === 'number' ? metric.toLocaleString() : metric}
        </div>
      </div>
    </motion.div>
  );
};

const SectionTitle = ({ icon: Icon, children, subtitle }) => (
  <div className="mb-5">
    <h2 className="text-xl md:text-2xl font-heading font-bold flex items-center gap-2">
      <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
      {children}
    </h2>
    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

const CommunityStatsPage = () => {
  const navigate = useNavigate();
  const { nickname, avatarId } = useGameStore();
  const [data, setData] = React.useState(null);
  const [percentile, setPercentile] = React.useState(null);
  const [allRankings, setAllRankings] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [rankSearch, setRankSearch] = React.useState('');
  const [rankPage, setRankPage] = React.useState(1);
  const [flashRank, setFlashRank] = React.useState(null);
  const [jumpToken, setJumpToken] = React.useState(0);
  const RANK_PAGE_SIZE = 25;

  // Filter + paginate the global rankings
  const filteredRankings = React.useMemo(() => {
    const q = rankSearch.trim().toLowerCase();
    if (!q) return allRankings;
    return allRankings.filter((p) => p.nickname.toLowerCase().includes(q));
  }, [allRankings, rankSearch]);

  const totalRankPages = Math.max(1, Math.ceil(filteredRankings.length / RANK_PAGE_SIZE));
  const pagedRankings = React.useMemo(
    () => filteredRankings.slice((rankPage - 1) * RANK_PAGE_SIZE, rankPage * RANK_PAGE_SIZE),
    [filteredRankings, rankPage]
  );

  // Index of the logged-in player within the full (unfiltered) rankings
  const myRankIndex = React.useMemo(() => {
    if (!nickname) return -1;
    return allRankings.findIndex((p) => p.nickname.toLowerCase() === nickname.toLowerCase());
  }, [allRankings, nickname]);

  // Reset to first page whenever the search query changes
  React.useEffect(() => { setRankPage(1); }, [rankSearch]);

  // Jump to the logged-in player's rank: clears search, pages to their row, scrolls + flashes
  const handleJumpToMe = () => {
    if (myRankIndex < 0) return;
    setRankSearch('');
    setJumpToken((t) => t + 1);
  };

  React.useEffect(() => {
    if (jumpToken === 0 || myRankIndex < 0) return;
    const targetPage = Math.floor(myRankIndex / RANK_PAGE_SIZE) + 1;
    setRankPage(targetPage);
    const myRank = allRankings[myRankIndex].rank;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid='global-rank-row-${myRank}']`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashRank(myRank);
      setTimeout(() => setFlashRank(null), 1800);
    }, 250);
    return () => clearTimeout(timer);
  }, [jumpToken]);

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const reqs = [
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/community-stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/all-rankings`),
        ];
        if (nickname) {
          reqs.push(fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/percentile`));
        }
        const [statsRes, rankingsRes, pctRes] = await Promise.all(reqs);
        if (statsRes.ok) setData(await statsRes.json());
        if (rankingsRes.ok) {
          const rk = await rankingsRes.json();
          if (rk.success) setAllRankings(rk.rankings);
        }
        if (pctRes && pctRes.ok) {
          const pct = await pctRes.json();
          if (pct.success) setPercentile(pct);
        }
      } catch (e) {
        console.error('Error fetching community stats:', e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [nickname]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        <p className="text-muted-foreground">Crunching the community numbers...</p>
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-4">
        <p className="text-muted-foreground">Stats are unavailable right now. Check back soon!</p>
        <Button onClick={() => navigate('/leaderboard')} className="bg-gradient-primary text-primary-foreground">
          Back to Leaderboard
        </Button>
      </div>
    );
  }

  const { overview, hall_of_fame, top_scorers, most_active, most_accurate, jailbird_kings,
    score_distribution, correct_distribution, accuracy_distribution, games_over_time } = data;

  const formatDate = (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const gradientStroke = (id, color) => (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );

  const pctData = percentile ? [
    { name: 'Points', value: percentile.percentiles.score_percentile, fill: PRIMARY },
    { name: 'Games', value: percentile.percentiles.games_percentile, fill: ACCENT },
    { name: 'Accuracy', value: percentile.percentiles.accuracy_percentile, fill: GOLD },
    { name: 'Bonuses', value: percentile.percentiles.bonuses_percentile, fill: GREEN },
  ] : [];

  return (
    <div className="min-h-screen bg-background p-2 sm:p-4" data-testid="community-stats-page">
      {/* Header */}
      <div className="container mx-auto max-w-6xl mb-6 sm:mb-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/leaderboard')} className="text-muted-foreground hover:text-foreground" data-testid="back-to-leaderboard-btn">
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')} className="text-muted-foreground hover:text-foreground hidden sm:flex">
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
          <Button
            onClick={() => {
              const stored = localStorage.getItem('jailbirds_nickname');
              navigate(stored ? '/game' : '/');
            }}
            className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
            data-testid="play-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Play
          </Button>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="flex justify-center gap-3 mb-4">
            <BirdMascot size={56} color="#10B981" />
            <BirdMascot size={56} color="#FACC15" />
            <BirdMascot size={56} color="#FF6B6B" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-2">
            Community <span className="text-gradient-primary">Stats</span>
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Every guess, every game, every JailBird — see how the whole flock stacks up.
          </p>
        </motion.div>
      </div>

      <div className="container mx-auto max-w-6xl space-y-10 sm:space-y-14">
        {/* Overview stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon={Gamepad2} label="Games Played" value={overview.total_games} accent={PRIMARY} delay={0.05} />
          <StatCard icon={Users} label="Total Players" value={overview.total_players} accent={ACCENT} delay={0.1} />
          <StatCard icon={Zap} label="Points Scored" value={overview.total_points} accent={GOLD} delay={0.15} />
          <StatCard icon={Target} label="Community Accuracy" value={overview.overall_accuracy} accent={GREEN} delay={0.2} suffix="%" />
        </div>

        {/* Secondary by-the-numbers */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon={Star} label="Perfect Games (15/15)" value={overview.perfect_games} accent={GOLD} delay={0.05} />
          <StatCard icon={Sparkles} label="JailBird Bonuses Won" value={overview.total_bonuses} accent={ACCENT} delay={0.1} />
          <StatCard icon={Flame} label="Highest Single Score" value={overview.highest_score} accent={PRIMARY} delay={0.15} />
          <StatCard icon={Activity} label="Questions Answered" value={overview.total_questions} accent={GREEN} delay={0.2} />
        </div>

        {/* 10 Most Reacted Mugshots */}
        <MostReactedGallery />

        {/* Hall of Fame */}
        <section>
          <SectionTitle icon={Crown} subtitle="The all-time legends of the flock">Hall of Fame</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hall_of_fame.top_scorer && (
              <HallOfFameCard icon={Trophy} title="Top Scorer" player={hall_of_fame.top_scorer}
                metric={hall_of_fame.top_scorer.total_score} metricLabel="total points" accent={GOLD} delay={0.05} />
            )}
            {hall_of_fame.most_active && (
              <HallOfFameCard icon={Gamepad2} title="Most Active" player={hall_of_fame.most_active}
                metric={hall_of_fame.most_active.games_played} metricLabel="games played" accent={PRIMARY} delay={0.1} />
            )}
            {hall_of_fame.most_accurate && (
              <HallOfFameCard icon={Target} title="Sharpest Eye" player={hall_of_fame.most_accurate}
                metric={`${hall_of_fame.most_accurate.accuracy}%`} metricLabel="accuracy (3+ games)" accent={GREEN} delay={0.15} />
            )}
            {hall_of_fame.jailbird_king && (
              <HallOfFameCard icon={Sparkles} title="JailBird King" player={hall_of_fame.jailbird_king}
                metric={hall_of_fame.jailbird_king.bonuses} metricLabel="bonuses won" accent={ACCENT} delay={0.2} />
            )}
            {hall_of_fame.highest_single_game && (
              <HallOfFameCard icon={Flame} title="Best Single Game" player={hall_of_fame.highest_single_game}
                metric={hall_of_fame.highest_single_game.score} metricLabel="points in one game" accent={GOLD} delay={0.25} />
            )}
          </div>
        </section>

        {/* Your standing */}
        {percentile && (
          <section>
            <SectionTitle icon={Award} subtitle="See how you rank against the entire community">Where You Stand</SectionTitle>
            <Card className="p-5 sm:p-6 bg-gradient-card border-primary/30">
              <div className="grid lg:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <BirdAvatar avatarId={avatarId} size={48} />
                    <div>
                      <div className="font-heading font-bold text-lg">{nickname}</div>
                      <div className="text-xs text-muted-foreground">vs {percentile.percentiles.total_players.toLocaleString()} players</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Points', val: percentile.percentiles.score_percentile, color: PRIMARY },
                      { label: 'Games Played', val: percentile.percentiles.games_percentile, color: ACCENT },
                      { label: 'Accuracy', val: percentile.percentiles.accuracy_percentile, color: GOLD },
                      { label: 'JailBird Bonuses', val: percentile.percentiles.bonuses_percentile, color: GREEN },
                    ].map((m) => (
                      <div key={m.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">{m.label}</span>
                          <span className="font-semibold" style={{ color: m.color }}>Top {Math.max(1, 100 - m.val)}%</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                          <motion.div className="h-full rounded-full" style={{ backgroundColor: m.color }}
                            initial={{ width: 0 }} animate={{ width: `${m.val}%` }} transition={{ duration: 1, ease: 'easeOut' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart innerRadius="25%" outerRadius="100%" data={pctData} startAngle={90} endAngle={-270}>
                      <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                      <RadialBar background={{ fill: 'rgba(255,255,255,0.05)' }} dataKey="value" cornerRadius={8} />
                      <Tooltip content={<ChartTooltip suffix="th pct" />} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          </section>
        )}

        {/* Activity over time */}
        {games_over_time.length > 0 && (
          <section>
            <SectionTitle icon={TrendingUp} subtitle="Games played per day across the community">Activity Trend</SectionTitle>
            <Card className="p-4 sm:p-6 bg-gradient-card border-border">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={games_over_time} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    {gradientStroke('gamesGradient', PRIMARY)}
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tickFormatter={formatDate} stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} />
                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip content={<ChartTooltip />} labelFormatter={formatDate} />
                    <Area type="monotone" dataKey="games" name="Games" stroke={PRIMARY} strokeWidth={2.5} fill="url(#gamesGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </section>
        )}

        {/* Distributions */}
        <section className="grid lg:grid-cols-2 gap-4 md:gap-6">
          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={BarChart3} subtitle="How single-game scores break down">Score Distribution</SectionTitle>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={score_distribution} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="range" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} angle={-25} textAnchor="end" height={50} interval={0} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip suffix=" games" />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="count" name="Games" radius={[6, 6, 0, 0]}>
                    {score_distribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={Target} subtitle="Correct answers per game (out of 15)">Guessing Skill</SectionTitle>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={correct_distribution} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="correct" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip suffix=" games" />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} labelFormatter={(l) => `${l} correct`} />
                  <Bar dataKey="count" name="Games" radius={[6, 6, 0, 0]}>
                    {correct_distribution.map((d, i) => (
                      <Cell key={i} fill={d.correct === 15 ? GOLD : d.correct >= 10 ? GREEN : d.correct >= 5 ? PRIMARY : ACCENT} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </section>

        {/* Leaderboards by category */}
        <section className="grid lg:grid-cols-2 gap-4 md:gap-6">
          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={Trophy} subtitle="Top 10 by total points">Points Leaders</SectionTitle>
            <div className="space-y-2.5">
              {top_scorers.map((p, i) => (
                <RankRow key={i} player={p} rank={i + 1} metric={p.total_score} metricLabel={`${p.games_played} games • ${p.accuracy}%`} accent={GOLD} />
              ))}
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={Gamepad2} subtitle="Top 10 by games played">Most Dedicated</SectionTitle>
            <div className="space-y-2.5">
              {most_active.map((p, i) => (
                <RankRow key={i} player={p} rank={i + 1} metric={p.games_played} metricLabel={`${p.total_score.toLocaleString()} pts • ${p.accuracy}%`} accent={PRIMARY} />
              ))}
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={Target} subtitle="Top 10 by accuracy (3+ games)">Sharpest Detectives</SectionTitle>
            <div className="space-y-2.5">
              {most_accurate.length > 0 ? most_accurate.map((p, i) => (
                <RankRow key={i} player={p} rank={i + 1} metric={`${p.accuracy}%`} metricLabel={`${p.games_played} games`} accent={GREEN} />
              )) : <div className="text-center py-8 text-muted-foreground text-sm">Not enough games played yet.</div>}
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-gradient-card border-border">
            <SectionTitle icon={Sparkles} subtitle="Top 10 by JailBird bonuses won">Bonus Hunters</SectionTitle>
            <div className="space-y-2.5">
              {jailbird_kings.length > 0 ? jailbird_kings.map((p, i) => (
                <RankRow key={i} player={p} rank={i + 1} metric={p.bonuses} metricLabel={`${p.games_played} games`} accent={ACCENT} />
              )) : <div className="text-center py-8 text-muted-foreground text-sm">No JailBird bonuses won yet. Be the first!</div>}
            </div>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center py-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-3">
              Think you can <span className="text-gradient-primary">climb the ranks?</span>
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Every game you play moves these numbers. Jump in and leave your mark on the flock.
            </p>
            <Button
              size="lg"
              onClick={() => {
                const stored = localStorage.getItem('jailbirds_nickname');
                navigate(stored ? '/game' : '/');
              }}
              className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-lg px-8"
              data-testid="cta-play-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Play Now
            </Button>
          </motion.div>
        </section>
      </div>

      {/* Global Leaderboard Rankings - full ranked list of every player */}
      <div className="container mx-auto max-w-6xl mt-2 sm:mt-6">
        <section data-testid="global-rankings-section">
          <SectionTitle icon={Trophy} subtitle={`Every detective who has ever played — ${allRankings.length.toLocaleString()} players ranked by all-time points`}>
            Global Leaderboard Rankings
          </SectionTitle>

          {/* Tier legend */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
            {TIERS.map((t) => (
              <div key={t.name} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{ borderColor: `${t.color}55`, backgroundColor: `${t.color}14` }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                <span className="text-xs font-semibold" style={{ color: t.color }}>{t.name}</span>
                <span className="text-[10px] text-muted-foreground">{t.min.toLocaleString()}+</span>
              </div>
            ))}
          </div>

          {/* Search box */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                value={rankSearch}
                onChange={(e) => setRankSearch(e.target.value)}
                placeholder="Search for a player by name..."
                className="pl-9 pr-9 bg-card/60 border-border"
                data-testid="rankings-search-input"
              />
              {rankSearch && (
                <button
                  onClick={() => setRankSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="rankings-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {myRankIndex >= 0 && (
              <Button
                variant="outline"
                onClick={handleJumpToMe}
                className="border-primary/40 text-primary hover:bg-primary/10 shrink-0"
                data-testid="jump-to-my-rank-btn"
              >
                <Crosshair className="w-4 h-4 mr-2" />
                Jump to my rank
                <span className="ml-2 text-xs font-bold px-1.5 py-0.5 rounded bg-primary/20">#{allRankings[myRankIndex].rank}</span>
              </Button>
            )}
          </div>

          <Card className="p-3 sm:p-5 bg-gradient-card border-border">
            {allRankings.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">No players yet. Be the first to make the rankings!</div>
            ) : filteredRankings.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm" data-testid="rankings-no-results">
                No players found matching "{rankSearch}".
              </div>
            ) : (
              <>
              <div className="space-y-1.5" data-testid="rankings-list">
                {pagedRankings.map((p) => {
                  const tier = getTier(p.total_score);
                  const medal = p.rank === 1 ? '🥇' : p.rank === 2 ? '🥈' : p.rank === 3 ? '🥉' : null;
                  const isMe = nickname && p.nickname.toLowerCase() === nickname.toLowerCase();
                  const isFlashing = flashRank === p.rank;
                  return (
                    <div
                      key={p.rank}
                      data-testid={`global-rank-row-${p.rank}`}
                      className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-lg border-l-4 transition-all hover:translate-x-1 ${isMe ? 'ring-1 ring-primary/50' : ''} ${isFlashing ? 'ring-2 ring-primary animate-pulse' : ''}`}
                      style={{
                        borderLeftColor: tier.color,
                        backgroundColor: isMe ? `${PRIMARY}1A` : `${tier.color}0D`,
                      }}
                    >
                      <div className="w-8 sm:w-10 text-center shrink-0">
                        {medal ? <span className="text-base sm:text-lg">{medal}</span> : <span className="text-xs sm:text-sm font-bold text-muted-foreground">#{p.rank}</span>}
                      </div>
                      <BirdAvatar avatarId={p.avatar_id || 1} size={30} animate={false} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold truncate text-sm sm:text-base">{p.nickname}</span>
                          {isMe && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/20 text-primary shrink-0">YOU</span>}
                          <span className="hidden sm:inline text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${tier.color}22`, color: tier.color }}>{tier.name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.games_played} {p.games_played === 1 ? 'game' : 'games'} • {p.accuracy}% acc
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading font-bold text-sm sm:text-lg whitespace-nowrap" style={{ color: tier.color }}>
                          {p.total_score.toLocaleString()}
                        </div>
                        <div className="text-[10px] text-muted-foreground hidden sm:block">points</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination controls */}
              {totalRankPages > 1 && (
                <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-border" data-testid="rankings-pagination">
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    Showing {((rankPage - 1) * RANK_PAGE_SIZE) + 1}-{Math.min(rankPage * RANK_PAGE_SIZE, filteredRankings.length)} of {filteredRankings.length.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRankPage((p) => Math.max(1, p - 1))}
                      disabled={rankPage === 1}
                      data-testid="rankings-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Prev</span>
                    </Button>
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap px-1">
                      {rankPage} / {totalRankPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRankPage((p) => Math.min(totalRankPages, p + 1))}
                      disabled={rankPage === totalRankPages}
                      data-testid="rankings-next-btn"
                    >
                      <span className="hidden sm:inline mr-1">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </Card>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 backdrop-blur-sm py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/about" className="text-primary hover:underline font-medium">About</a>
              <span className="text-muted-foreground">•</span>
              <a href="/legal" className="text-primary hover:underline font-medium">Privacy & Legal</a>
              <span className="text-muted-foreground">•</span>
              <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-medium">Contact</a>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © 2025 JailBirds. Owned and operated by M.Panepinto. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CommunityStatsPage;
